import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Field, Formik, FormikProps, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import apis from '../../libs/services'
import { setSessionItem } from '../../utils/sessionStorage'
import { useAuth } from '../../hooks/useAuth'
import { Button, Input } from '../../components/atoms'
import logo from '../../assets/images/logo.svg'
import { Modal } from '../../components/molecules'
import { useAuthContext } from '../../contexts/AuthContext'
import { toast } from 'react-toastify'
import useSetFocusById from '../../hooks/useSetFocus'
import ControlHighlight from '../../components/molecules/KeyboardControls/ControlHighlight'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import KeyboardControl from '../../components/molecules/KeyboardControls/KeyboardControl'
import { HighlightEffect } from '../../enums/KeyboardControls'
import ContactLinks from '../../components/molecules/ContactLinks/ContactLinks'
import { isResponseNotFound, isResponseSuccessful } from '../../utils/request'
import { MobileDetectionWrapper } from '../../components/molecules/MobileDetectionWrapper/MobileDetectionWrapper'

interface FormValues {
  email: string
}

const initialValues = {
  email: ''
}

const WelcomeFormSchema = Yup.object().shape({
  email: Yup.string()
    .email('Input valid email!')
    .required('This field is required!')
})

export enum FocusableElement {
  InputEmail = 'input-email',
  ButtonContinue = 'button-continue',
  LinkContact = 'link-contact',
}

export const stringErrorNoRegisteredUser = 'Not a registered user'
export const stringErrorCheckingUserStatus = 'Error checking user status'

function Welcome (): ReactElement {
  const navigate = useNavigate()
  const { userProfile } = useAuth()
  const { getAuthenticatedUser } = useAuthContext()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [setFocusById] = useSetFocusById()
  const keyboardControls = useKeyboardControls()

  // Check to see if a user is authenticated.  If so, automatically
  // bring them into the app.
  useEffect(() => {
    const checkIsUserAuthenticated = async (): Promise<void> => {
      const user = await getAuthenticatedUser()
      if (
        user !== null &&
        user !== undefined &&
        userProfile !== null &&
        userProfile !== undefined &&
        user.signInDetails?.loginId === userProfile?.email
      ) {
        if (userProfile?.role.slug === 'admin') {
          navigate('/dashboard/all-assigned-students')
        } else if (userProfile != null) {
          navigate('/user-dashboard')
        }
      }
    }
    void checkIsUserAuthenticated()

    setFocusById(FocusableElement.InputEmail)
  }, [getAuthenticatedUser, setFocusById, navigate, userProfile])

  const showErrorToUser = (actions: any, errorMessage: string): void => {
    toast.error(errorMessage, { position: toast.POSITION.TOP_CENTER })
    actions.setSubmitting(false)
    actions.setFieldError(
      'email',
      'Something went wrong!'
    )
  }

  const handleSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>): Promise<void> => {
    try {
      const res = await apis.checkUserStatus(values.email, true)
      actions.setSubmitting(false)
      if (res === null || res === undefined) {
        showErrorToUser(actions, stringErrorCheckingUserStatus)
      } else if (res.data?.data?.activation_status === false) {
        const response = await apis.beginUserActivation(values.email)
        if (isResponseSuccessful(response)) {
          openModal()
        }
      } else if (isResponseSuccessful(res)) {
        setSessionItem('email', values.email)
        navigate('/login')
      } else if (isResponseNotFound(res)) {
        actions.setErrors({ email: stringErrorNoRegisteredUser })
      } else {
        showErrorToUser(actions, stringErrorCheckingUserStatus)
      }
    } catch (err: any) {
      showErrorToUser(actions, err.message)
    }
  }

  const openModal = (): void => {
    setIsModalOpen(true)
  }

  return (
    <MobileDetectionWrapper>
      <div className='bg-black min-h-screen font-primary items-center flex justify-center'>
        <header className='absolute left-[40px] top-[40px]' aria-hidden='true'>
          <img src={logo} className='w-[138px]' alt='logo' />
        </header>
        <section>
          <Formik
            initialValues={initialValues}
            validationSchema={WelcomeFormSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              touched,
              isSubmitting,
              status,
              values
            }: FormikProps<FormValues>) => (
              <>
                {isModalOpen
                  ? (
                    <Modal tall>
                      <h1 className='sr-only'>Sign Up - Check your email</h1>
                      <h2 className='font-bold capitalize text-[26px] text-center'>
                        sign up
                      </h2>
                      <p className='pt-14 pb-16 font-bold text-[38px] leading-none min-h-1024:pt-20 min-h-1024:pb-32'>
                        Check your email
                      </p>
                      <p className='text-[26px] w-[430px] leading-[3rem] font-thin text-center'>
                        Click the button in the email sent to{' '}
                        <span className='text-dopage-darkGreen'>{values.email}</span>
                        <br />
                        to complete your onboarding.
                      </p>
                      <div className='max-w-[440px] w-full min-h-1024:mt-[6.5rem]'>
                        <Button
                          className='invisible font-primary bg-dopage-primary w-full h-[86px] diamond-btn font-bold mt-[40px] flex justify-center items-center'
                        >
                          <span className='text-[26px] text-neutral-090'>
                            Resend Email
                          </span>
                        </Button>
                        <ContactLinks
                          id={FocusableElement.LinkContact}
                        />
                      </div>
                    </Modal>
                    )
                  : (
                    <Modal>
                      <h1 className='font-bold capitalize text-[26px] text-dopage-lightGreen text-center'>
                        log in or sign up
                      </h1>

                      <div className='max-w-[440px] w-full mt-[100px]'>
                        <p className='font-bold text-[38px] leading-none text-dopage-lightGreen'>
                          Welcome to DopaGE
                        </p>
                        <Form>
                          <label aria-label='email'>
                            <Field
                              name='email' type='email'
                            >
                              {({
                                field
                              }: any) => (
                                <div
                                  className='mt-[26px]'
                                >
                                  <ControlHighlight
                                    active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.InputEmail)}
                                    highlightEffect={HighlightEffect.InputOutline}
                                    data-testid={`highlight-${FocusableElement.InputEmail}`}
                                    activeClassName='mb-[12px]'
                                  >
                                    <KeyboardControl>
                                      <Input
                                        id={FocusableElement.InputEmail}
                                        data-testid={FocusableElement.InputEmail}
                                        className='w-full py-[30px] px-[27px] text-[26px]'
                                        placeholder='your@email.com'
                                        {...field}
                                      />
                                    </KeyboardControl>
                                  </ControlHighlight>
                                </div>
                              )}
                            </Field>
                          </label>
                          {errors.email != null && errors.email !== '' && touched.email === true
                            ? (
                              <div
                                className='text-[#ff0000] pt-1'
                                aria-label={`Error message: ${errors.email}`}
                              >
                                {errors.email}
                              </div>
                              )
                            : null}
                          <ControlHighlight
                            active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonContinue)}
                            highlightEffect={HighlightEffect.ButtonHexOutline}
                            data-testid={`highlight-${FocusableElement.ButtonContinue}`}
                          >
                            <KeyboardControl>
                              <Button
                                className='h-[86px] w-full diamond-btn font-bold mt-[28px] flex justify-center items-center'
                                data-testid={FocusableElement.ButtonContinue}
                                disabled={isSubmitting}
                                id={FocusableElement.ButtonContinue}
                                loading={isSubmitting}
                                type='submit'
                              >
                                <span className='text-black text-[26px]'>
                                  Continue
                                </span>
                              </Button>
                            </KeyboardControl>
                          </ControlHighlight>
                          <ContactLinks
                            id={FocusableElement.LinkContact}
                          />
                        </Form>
                      </div>
                      <div className='text-[#ff0000] pt-1'>{status}</div>
                    </Modal>
                    )}
              </>
            )}
          </Formik>
        </section>
      </div>
    </MobileDetectionWrapper>
  )
}

export default Welcome
